import React, { forwardRef, useId } from "react"

import * as Headless from "@headlessui/react"
import clsx from "clsx"

import { Description, ErrorMessage, Field, Label } from "./fieldset"

type HeadlessSwitchProps = React.ComponentPropsWithoutRef<typeof Headless.Switch>

type ForwardedSwitchProps = Omit<
  HeadlessSwitchProps,
  | `aria-describedby`
  | `aria-errormessage`
  | `aria-invalid`
  | `aria-label`
  | `aria-labelledby`
  | `children`
  | `className`
  | `id`
>

export type ToggleFieldProps = ForwardedSwitchProps & {
  label?: React.ReactNode
  labelSrOnly?: boolean
  description?: React.ReactNode
  error?: React.ReactNode
  className?: string
  labelPosition?: `start` | `end`
}

const IS_DEV_BUILD = process.env.NODE_ENV !== `production`

export const ToggleField = forwardRef(function ToggleField(
  {
    label,
    labelSrOnly = false,
    description,
    error,
    className,
    disabled,
    labelPosition = `end`,
    ...switchProps
  }: ToggleFieldProps,
  ref: React.ForwardedRef<HTMLButtonElement>,
) {
  const errorId = useId()
  const errorMessageId = error ? errorId : undefined

  if (IS_DEV_BUILD && !label && !labelSrOnly) {
    // eslint-disable-next-line no-console
    console.error(
      `<ToggleField> requires a 'label' prop, or 'labelSrOnly' must be true to acknowledge the field is intentionally unlabelled.`,
    )
  }

  const hasVisibleText = (!labelSrOnly && Boolean(label)) || Boolean(description)
  const hasSrOnlyLabelOnly = labelSrOnly && Boolean(label) && !description

  const textContent = hasVisibleText ? (
    <span
      className={clsx(
        `min-w-0`,
        labelPosition === `start` ? `flex grow flex-col` : `text-sm`,
      )}
    >
      {label ? (
        <Label
          className={clsx(
            labelSrOnly ? `sr-only` : undefined,
            labelPosition === `end` ? `inline text-sm/6` : undefined,
          )}
        >
          {label}
        </Label>
      ) : null}
      {label && description && !labelSrOnly && labelPosition === `end`
        ? ` `
        : null}
      {description ? (
        <Description
          className={clsx(
            labelPosition === `start` ? `text-sm` : `inline text-sm/6`,
          )}
        >
          {description}
        </Description>
      ) : null}
    </span>
  ) : null

  return (
    <Field className={clsx(className, `space-y-3`)} disabled={disabled}>
      {hasSrOnlyLabelOnly ? <Label className="sr-only">{label}</Label> : null}
      <div
        className={clsx(
          `flex gap-3`,
          labelPosition === `start`
            ? `items-start justify-between`
            : `items-center`,
        )}
      >
        {labelPosition === `start` ? textContent : null}
        <Headless.Switch
          {...switchProps}
          ref={ref}
          data-slot="control"
          aria-invalid={error ? true : undefined}
          aria-errormessage={errorMessageId}
          data-invalid={error ? true : undefined}
          className={clsx(
            `group relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full bg-surface p-0.5 ring-1 ring-border ring-inset`,
            `transition-colors duration-200 ease-in-out`,
            `focus:not-data-focus:outline-hidden data-focus:outline-2 data-focus:outline-offset-2 data-focus:outline-focus-ring`,
            `data-hover:ring-primary/40`,
            `data-checked:bg-primary data-checked:ring-primary`,
            `data-invalid:ring-danger data-hover:data-invalid:ring-danger`,
            `data-disabled:cursor-not-allowed data-disabled:opacity-50`,
            `data-disabled:data-checked:bg-primary/60 data-disabled:data-checked:ring-primary/60`,
          )}
        >
          <span
            aria-hidden="true"
            className={clsx(
              `pointer-events-none size-5 rounded-full bg-background shadow-xs ring-1 ring-border`,
              `translate-x-0 transition-transform duration-200 ease-in-out`,
              `group-data-checked:translate-x-5`,
            )}
          />
        </Headless.Switch>
        {labelPosition === `end` ? textContent : null}
      </div>
      {error ? <ErrorMessage id={errorMessageId}>{error}</ErrorMessage> : null}
    </Field>
  )
})
